import { Gauge } from "@phosphor-icons/react";

const GRADE_COLOR = {
  Excellent: "#059669",
  "On Track": "#2563EB",
  Watch: "#D97706",
  "At Risk": "#E11D48",
};

export const gradeColor = (grade) => GRADE_COLOR[grade] || "#71717a";

// Circular Cost Health gauge with a "why" breakdown. `health` = { score, grade, breakdown:[{label, points}] }.
export function HealthGauge({ health, testId = "project-health-gauge" }) {
  if (!health || typeof health.score !== "number") return null;
  const color = GRADE_COLOR[health.grade] || "#71717a";
  const r = 54;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, health.score));
  const dash = (pct / 100) * circ;
  const factors = (health.breakdown || []).filter((b) => b.points < 0);

  return (
    <div data-testid={testId} className="border border-border mb-8">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary">
        <Gauge size={18} weight="bold" />
        <span className="font-heading font-bold">Cost Health Score</span>
      </div>
      <div className="p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-8">
        <div className="relative shrink-0" style={{ width: 148, height: 148 }}>
          <svg width="148" height="148" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="74" cy="74" r={r} fill="none" stroke="#E4E4E7" strokeWidth="12" />
            <circle
              cx="74" cy="74" r={r} fill="none" stroke={color} strokeWidth="12"
              strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
              style={{ transition: "stroke-dasharray 0.8s ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-mono-fig text-4xl font-semibold tabular-nums" style={{ color }} data-testid="health-gauge-score">{health.score}</span>
            <span className="text-xs text-muted-foreground">/ 100</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 w-full">
          <span className="inline-flex items-center text-sm px-3 py-1 border font-medium" style={{ borderColor: color, color }} data-testid="health-gauge-grade">
            {health.grade}
          </span>
          <p className="label-caps mt-4 mb-2">Why this score</p>
          <div className="space-y-2">
            {factors.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="health-why-clean">No penalties — this project is tracking to plan.</p>
            ) : (
              factors.map((b) => (
                <div key={b.label} className="flex items-center justify-between text-sm border-b border-border pb-1.5"
                  data-testid={`health-factor-${b.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <span className="text-muted-foreground">{b.label}</span>
                  <span className="font-mono-fig font-semibold text-rose-600 tabular-nums">{b.points}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
